import { TZDate } from "@date-fns/tz";

/**
 * Время в поясе салона. Все моменты с сервера в UTC (`Date`); экран показывает местное время салона,
 * а не телефона (мастер может открыть кабинет в поездке).
 */

const intl: Record<string, string> = { "sr-Latn": "sr-Latn-RS", en: "en-GB", ru: "ru-RU" };
export const intlLocale = (lang: string): string => intl[lang] ?? "sr-Latn-RS";

/** Местная дата `YYYY-MM-DD` момента. */
export function dayKey(instant: Date, tz: string): string {
  const d = new TZDate(instant.getTime(), tz);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const parts = (key: string) => key.split("-").map(Number) as [number, number, number];

/** Момент «в этот местный день в HH:MM». */
export function atLocal(key: string, hhmm: string, tz: string): Date {
  const [y, m, d] = parts(key);
  const [h, min] = hhmm.split(":").map(Number) as [number, number];
  return new Date(new TZDate(y, m - 1, d, h, min, tz).getTime());
}

/** Начало местного дня. */
export const dayStart = (key: string, tz: string): Date => atLocal(key, "00:00", tz);

export function addDays(key: string, days: number): string {
  const [y, m, d] = parts(key);
  const x = new Date(Date.UTC(y, m - 1, d + days));
  return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, "0")}-${String(x.getUTCDate()).padStart(2, "0")}`;
}

/** Понедельник недели местной даты. */
export function weekStart(key: string): string {
  const [y, m, d] = parts(key);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return addDays(key, wd === 0 ? -6 : 1 - wd);
}

export function fmtTime(instant: Date, tz: string, lang: string): string {
  return new Intl.DateTimeFormat(intlLocale(lang), { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(instant);
}

/** Местное `HH:MM` момента, для полей ввода времени. */
export const hhmmOf = (instant: Date, tz: string): string => fmtTime(instant, tz, "en");

export function fmtDay(key: string, lang: string, opts: Intl.DateTimeFormatOptions = { weekday: "short", day: "numeric", month: "short" }): string {
  const [y, m, d] = parts(key);
  return new Intl.DateTimeFormat(intlLocale(lang), { timeZone: "UTC", ...opts }).format(new Date(Date.UTC(y, m - 1, d, 12)));
}

export function fmtDateTime(instant: Date, tz: string, lang: string): string {
  return `${fmtDay(dayKey(instant, tz), lang)} ${fmtTime(instant, tz, lang)}`;
}

export function fmtMoney(amount: number, lang: string): string {
  return `${new Intl.NumberFormat(intlLocale(lang)).format(amount)} RSD`;
}

/** «5 мин назад», «вчера»: для ленты. */
export function fmtAgo(instant: Date, now: Date, tz: string, lang: string): string {
  const diff = now.getTime() - instant.getTime();
  const rtf = new Intl.RelativeTimeFormat(intlLocale(lang), { numeric: "auto", style: "short" });
  if (diff < 60_000) return rtf.format(0, "minute");
  if (diff < 3_600_000) return rtf.format(-Math.floor(diff / 60_000), "minute");
  if (dayKey(instant, tz) === dayKey(now, tz)) return fmtTime(instant, tz, lang);
  return fmtDay(dayKey(instant, tz), lang, { day: "numeric", month: "short" });
}

/** Ключ идемпотентности для ручной записи (повторное нажатие даёт ту же запись). */
export const newKey = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
